import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiError, confirmSheets, getPages, getSheets, putSheets } from "@/api/client";
import type { Page, Run, Sheet, Sheets } from "@/api/client";

const POLL_MS = 2000;
const SAVE_MS = 600;

export interface SheetsState {
  sheets: Sheet[];
  /** False while the set is still being read. */
  ready: boolean;
  loaded: boolean;
  selected: ReadonlySet<number>;
  /** Page indexes that already have a classification, so selecting them costs nothing. */
  classified: ReadonlySet<number>;
  error: string | null;
  saving: boolean;
  saveError: string | null;
  toggle: (pageIndex: number) => void;
  setMany: (pageIndexes: readonly number[], on: boolean) => void;
  confirm: () => Promise<Run | null>;
  confirming: boolean;
  /** The server's refusal, with its status so the screen can tell 403 from 422. */
  confirmError: { status: number; detail: string } | null;
}

const message = (err: unknown, fallback: string) =>
  err instanceof ApiError ? err.detail : fallback;

/**
 * The sheets of a set and which of them the take-off will use. Polls while the set is
 * being read; saves the selection a moment after the last change; confirms and starts
 * the run.
 */
export function useSheets(documentId: string, status: string | undefined): SheetsState {
  const [data, setData] = useState<Sheets | null>(null);
  const [pages, setPages] = useState<Page[]>([]);
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<SheetsState["confirmError"]>(null);
  const touched = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const latest = useRef<ReadonlySet<number>>(selected);
  latest.current = selected;

  const reading = status === undefined || status === "uploaded" || status === "ingesting";

  useEffect(() => {
    let cancelled = false;
    let poll: ReturnType<typeof setTimeout> | undefined;

    const adopt = (next: Sheets) => {
      setData(next);
      setError(null);
      if (!touched.current) {
        setSelected(new Set(next.sheets.filter((s) => s.selected).map((s) => s.page_index)));
      }
    };

    const load = async () => {
      try {
        let listed: Page[] = [];
        if (!reading) {
          listed = (await getPages(documentId)).pages;
        }
        const next = await getSheets(documentId);
        if (cancelled) {
          return;
        }
        setPages(listed);
        adopt(next);
        if (!next.ready || reading) {
          poll = setTimeout(() => void load(), POLL_MS);
        }
      } catch (err) {
        if (!cancelled) {
          setError(message(err, "could not reach the API"));
          poll = setTimeout(() => void load(), POLL_MS * 2);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
      if (poll) {
        clearTimeout(poll);
      }
    };
  }, [documentId, reading]);

  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    },
    [],
  );

  const save = useCallback(
    async (next: ReadonlySet<number>) => {
      setSaving(true);
      try {
        await putSheets(
          documentId,
          [...next].sort((a, b) => a - b),
        );
        setSaveError(null);
      } catch (err) {
        setSaveError(message(err, "could not save the selection"));
      } finally {
        setSaving(false);
      }
    },
    [documentId],
  );

  const schedule = useCallback(
    (next: ReadonlySet<number>) => {
      touched.current = true;
      setSelected(next);
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = setTimeout(() => void save(next), SAVE_MS);
    },
    [save],
  );

  const toggle = useCallback(
    (pageIndex: number) => {
      const next = new Set(latest.current);
      if (next.has(pageIndex)) {
        next.delete(pageIndex);
      } else {
        next.add(pageIndex);
      }
      schedule(next);
    },
    [schedule],
  );

  const setMany = useCallback(
    (pageIndexes: readonly number[], on: boolean) => {
      const next = new Set(latest.current);
      for (const index of pageIndexes) {
        if (on) {
          next.add(index);
        } else {
          next.delete(index);
        }
      }
      schedule(next);
    },
    [schedule],
  );

  const classified = useMemo(
    () => new Set(pages.filter((page) => page.views.length > 0).map((page) => page.index)),
    [pages],
  );

  const confirm = useCallback(async (): Promise<Run | null> => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
    const list = [...latest.current].sort((a, b) => a - b);
    setConfirming(true);
    setConfirmError(null);
    try {
      return await confirmSheets(documentId, { selected: list });
    } catch (err) {
      setConfirmError({
        status: err instanceof ApiError ? err.status : 0,
        detail: message(err, "could not start the run"),
      });
      return null;
    } finally {
      setConfirming(false);
    }
  }, [documentId]);

  return {
    sheets: data?.sheets ?? [],
    ready: data?.ready ?? false,
    loaded: data !== null,
    selected,
    classified,
    error,
    saving,
    saveError,
    toggle,
    setMany,
    confirm,
    confirming,
    confirmError,
  };
}
